import React, { FC, useCallback, useEffect, useState } from 'react';

import { useSkipLinks } from '@evinced-private/ui-common';

import { WfaSessionsSkeleton } from 'src/apps/wfa/components/skeletons';
import { WfaSessionNotSyncedYetPopup } from 'src/apps/wfa/components/WfaSessionNotSyncedYetPopup';
import { useWfaLabelFilterOptionsQuery } from 'src/apps/wfa/hooks/useWfaLabelFilterOptionsQuery';
import { useWfaSessionsQuery } from 'src/apps/wfa/hooks/useWfaSessionsQuery';
import { useWfaUsageDataQuery } from 'src/apps/wfa/hooks/useWfaUsageDataQuery';
import { useWfaUserOptionsQuery } from 'src/apps/wfa/hooks/useWfaUserOptionsQuery';
import { WfaSessionsPageError } from 'src/apps/wfa/pages/error-pages/WfaSessionsPageError';
import { IWfaSessionRow } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { HorizontalDivider } from 'src/common/components/horizontal-divider/HorizontalDivider';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';
import { ILabelConfig } from 'src/common/types/LabelsTypes';

import { WfaSessionsPageHeader } from './sections/header/WfaSessionsHeader';
import { WfaSessionsTable } from './sections/table/WfaSessionsTable';
import { WfaSessionsUsage } from './sections/usage/WfaSessionsUsage';

import './WfaSessionsPage.scss';

export const WfaSessionsPage: FC = () => {
	const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);
	const [selectedRows, setSelectedRows] = useState<IWfaSessionRow[]>([]);
	const [tableKey, setTableKey] = useState(0);
	const { getTableStateById, onFilterChange } = useTableState();
	const { filters, tableSort, paginationInfo } = getTableStateById(WFA_SESSIONS_TABLE_ID);

	const { configuration } = useConfiguration();
	const labelsConfig = (configuration?.WFA?.PLATFORM_LABELS_LIST ?? []) as ILabelConfig[];
	const isLabelsToggleEnabled = Boolean(
		(configuration?.WFA?.TOGGLES as Record<string, boolean>)?.ENABLE_PLATFORM_LABELS
	);

	const { isNewPlatformEnabled } = usePlatformConfigStore();

	const handleClearSelection = useCallback((): void => {
		setSelectedRows([]);
		setTableKey((prev) => prev + 1);
	}, []);

	const handleSelectRow = useCallback((row: IWfaSessionRow, isSelect: boolean): void => {
		if (isSelect) {
			setSelectedRows((prev) => [...prev, row]);
		} else {
			setSelectedRows((prev) => prev.filter((r) => r !== row));
		}
	}, []);

	const handleSelectAll = useCallback((isSelect: boolean, rows: IWfaSessionRow[]): void => {
		setSelectedRows(isSelect ? rows : []);
	}, []);

	const userOptionsQuery = useWfaUserOptionsQuery();
	const labelOptionsQuery = useWfaLabelFilterOptionsQuery(labelsConfig, isLabelsToggleEnabled);

	const usageDataQuery = useWfaUsageDataQuery(filters);

	const sessionsQuery = useWfaSessionsQuery({
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		sort: tableSort?.[0],
		filters,
		labelsConfig
	});
	useSkipLinks(!usageDataQuery.isLoading && !sessionsQuery.isLoading);

	const isFilterOptionsLoading =
		userOptionsQuery.isLoading || (isLabelsToggleEnabled && labelOptionsQuery.isLoading);
	const isPrimaryLoading =
		isFilterOptionsLoading || usageDataQuery.isLoading || sessionsQuery.isLoading;

	useEffect(() => {
		if (!isPrimaryLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPrimaryLoading, showInitialSkeleton]);

	const unfilteredTotal = sessionsQuery.data?.unfilteredTotal ?? 0;

	if (sessionsQuery?.isError || usageDataQuery?.isError) {
		return <WfaSessionsPageError />;
	}

	if (!sessionsQuery?.isLoading && !sessionsQuery?.isPreviousData && unfilteredTotal === 0) {
		return (
			<>
				<WfaSessionsPageError isNoData />;
				<WfaSessionNotSyncedYetPopup />
			</>
		);
	}

	if (showInitialSkeleton) {
		return <WfaSessionsSkeleton />;
	}

	return (
		<div className="wfa-sessions-page">
			<WfaSessionsPageHeader
				filters={filters}
				onFilterChange={onFilterChange}
				labelOptions={labelOptionsQuery.data}
				userOptions={userOptionsQuery.data}
				isFilterOptionsLoading={isFilterOptionsLoading}
			/>
			<HorizontalDivider />
			<WfaSessionsUsage
				data={usageDataQuery.data}
				isLoading={usageDataQuery.isLoading}
				selectedPeriodLabel={filters.sessionsPeriod?.[0]?.label}
			/>
			<WfaSessionsTable
				sessionsQuery={sessionsQuery}
				unfilteredTotal={unfilteredTotal}
				isLabelsToggleEnabled={isLabelsToggleEnabled}
				isNewPlatformEnabled={isNewPlatformEnabled}
				selectedRows={selectedRows}
				tableKey={tableKey}
				onSelectRow={handleSelectRow}
				onSelectAll={handleSelectAll}
				onClearSelection={handleClearSelection}
			/>
			<WfaSessionNotSyncedYetPopup />
		</div>
	);
};
